/**
 * Booking provider — bridges every "Reserve a Chair" / "Book with X" button to
 * the salon's Square Appointments hosted booking page.
 */
import { createContext, useContext, useCallback } from "react";

const BookingContext = createContext(null);

// Hard-coded fallback so the redirect always works, even if an admin accidentally
// clears the field or pastes a partial URL. This URL is also the configured
// default in the backend; keep them in sync if you ever change the Square account.
const DEFAULT_SQUARE_BOOKING_URL =
  "https://book.squareup.com/appointments/87409466-cbe6-4b59-bf78-a3ea3e71ed7e/location/5XXYAXDDC0EC9/services";

// A URL is "good enough to use" only if it points at a specific appointments page.
// `https://book.squareup.com/` alone returns 404, so we treat anything missing
// the `/appointments/` segment as invalid and use the fallback.
const isValidBookingUrl = (u) =>
  typeof u === "string" && /^https?:\/\/.+\/appointments\//i.test(u);

export function BookingProvider({ children, settings }) {
  const raw = settings?.square_booking_url || "";
  const url = isValidBookingUrl(raw) ? raw : DEFAULT_SQUARE_BOOKING_URL;

  const openBooking = useCallback(() => {
    window.open(url, "_blank", "noopener,noreferrer");
  }, [url]);

  return (
    <BookingContext.Provider value={{ openBooking, bookingUrl: url }}>
      {children}
    </BookingContext.Provider>
  );
}

export const useBooking = () => {
  const ctx = useContext(BookingContext);
  if (!ctx) throw new Error("useBooking must be used inside BookingProvider");
  return ctx;
};
